import { Fragment, useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { Dialog, Transition } from "@headlessui/react";
import { useAuthStore } from "../../../stores/authStore";
import { motion, AnimatePresence } from "framer-motion";
import { SocialLoginButtons } from "./SocialLoginButtons";
import { SocialLoginStatus, SocialStatus } from "../../../components/common/SocialLoginStatus";
import { Logo } from "../../../components/common/Logo";
import { authService } from "../../../lib/api/auth";

type LoginFormData = {
    email: string;
    password: string;
};

interface LoginModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSwitchToRegister?: () => void;
}

export const LoginModal = ({ isOpen, onClose, onSwitchToRegister }: LoginModalProps) => {
    const { t } = useTranslation("common");
    const [searchParams, setSearchParams] = useSearchParams();

    const loginSchema = z.object({
        email: z.string().email(t("errors.invalidEmail") as string),
        password: z.string().min(1, t("errors.required") as string),
    });

    const navigate = useNavigate();
    const { login, isLoading, error, clearError } = useAuthStore();

    const {
        register,
        handleSubmit,
        formState: { errors },
        clearErrors,
        reset
    } = useForm<LoginFormData>({
        resolver: zodResolver(loginSchema),
        mode: "onBlur",
        reValidateMode: "onBlur"
    });


    useEffect(() => {
        if (!isOpen) {
            // Immediately clear errors to prevent them showing during transition
            clearErrors();
            clearError();

            // Delay resetting form values slightly to avoid flicker during closing transition
            const timer = setTimeout(() => {
                reset();
                setView('options');
                setMagicLinkEmail('');
                setMagicLinkSent(false);
                setMagicLinkError(null);
                setMagicLinkCooldown(0);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [isOpen, reset, clearError, clearErrors]);

    const onSubmit = async (data: LoginFormData) => {
        try {
            clearError();
            await login(data);

            const { user } = useAuthStore.getState();
            const skipVerification = window.__DASHBAL_CONFIG__?.skipEmailVerification;
            const redirectPath = (user && !user.emailVerified && !skipVerification)
                ? "/check-email"
                : "/boards";

            // The route change unmounts the modal — do not call onClose().
            // onClose() = parent's setSearchParams({}), whose internal
            // navigate("?") uses the parent's captured pathname ("/") and
            // would navigate back to "/", undoing this redirect.
            navigate(redirectPath, { replace: true });
        } catch (err) {
            // Error is already set in the store
        }
    };

    const [view, setView] = useState<'options' | 'email' | 'magiclink'>('options');
    const [socialStatus, setSocialStatus] = useState<SocialStatus | null>(null);
    const [socialProvider, setSocialProvider] = useState<string>('');
    const [magicLinkEmail, setMagicLinkEmail] = useState('');
    const [magicLinkSent, setMagicLinkSent] = useState(false);
    const [magicLinkLoading, setMagicLinkLoading] = useState(false);
    const [magicLinkError, setMagicLinkError] = useState<string | null>(null);
    const [magicLinkCooldown, setMagicLinkCooldown] = useState(0);

    useEffect(() => {
        if (magicLinkCooldown <= 0) return;
        const timer = setInterval(() => {
            setMagicLinkCooldown(s => Math.max(0, s - 1));
        }, 1000);
        return () => clearInterval(timer);
    }, [magicLinkCooldown]);

    const handleSocialLogin = async (provider: string, token: string) => {
        try {
            setSocialProvider(provider);
            setSocialStatus('processing');
            clearError();
            await useAuthStore.getState().socialLogin(provider, token);
            
            // Show success state briefly before closing
            setSocialStatus('success');
            
            setTimeout(() => {
                const { user } = useAuthStore.getState();
                const skipVerification = window.__DASHBAL_CONFIG__?.skipEmailVerification;
                const redirectPath = (user && !user.emailVerified && !skipVerification)
                    ? "/check-email"
                    : "/boards";

                // The route change unmounts the modal — do not call onClose().
                // onClose() = parent's setSearchParams({}), whose internal
                // navigate("?") uses the parent's captured pathname ("/") and
                // would navigate back to "/", undoing this redirect.
                navigate(redirectPath, { replace: true });

                // Reset status after a delay (if modal is reused)
                setTimeout(() => setSocialStatus(null), 500);
            }, 1200);
        } catch (err: any) {
            setSocialStatus('error');
            // Allow user to see error for a moment then go back to options
            setTimeout(() => {
                setSocialStatus(null);
            }, 3000);
            // Error handling is managed by the store
        }
    };

    const handleBack = () => {
        reset();
        clearError();
        clearErrors();
        setMagicLinkEmail('');
        setMagicLinkSent(false);
        setMagicLinkError(null);
        setView('options');
    };

    const applyCooldownFromResponse = (canResendAfter: string | null | undefined, fallbackSeconds = 60) => {
        if (canResendAfter) {
            const canResendAt = new Date(canResendAfter).getTime();
            setMagicLinkCooldown(Math.max(0, Math.ceil((canResendAt - Date.now()) / 1000)));
        } else {
            setMagicLinkCooldown(fallbackSeconds);
        }
    };

    const applyCooldownFromError = (err: any) => {
        const apiData = err?.response?.data?.data;
        if (apiData?.canResendAfter) {
            const canResendAt = new Date(apiData.canResendAfter).getTime();
            setMagicLinkCooldown(Math.max(0, Math.ceil((canResendAt - Date.now()) / 1000)));
            return true;
        }
        if (typeof apiData?.waitSeconds === 'number') {
            setMagicLinkCooldown(apiData.waitSeconds);
            return true;
        }
        return false;
    };

    const sendMagicLinkRequest = async (email: string) => {
        setMagicLinkLoading(true);
        setMagicLinkError(null);
        try {
            const response = await authService.sendMagicLink(email);
            setMagicLinkSent(true);
            applyCooldownFromResponse(response.data?.canResendAfter);
            return true;
        } catch (err: any) {
            const hadCooldown = applyCooldownFromError(err);
            const apiMessage = err?.response?.data?.error;
            setMagicLinkError(hadCooldown && apiMessage ? apiMessage : (t("auth.magicLinkSendError") as string));
            return false;
        } finally {
            setMagicLinkLoading(false);
        }
    };

    const handleSendMagicLink = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!magicLinkEmail || magicLinkLoading || magicLinkCooldown > 0) return;

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(magicLinkEmail)) {
            setMagicLinkError(t("errors.invalidEmail") as string);
            return;
        }

        await sendMagicLinkRequest(magicLinkEmail);
    };

    const handleResendMagicLink = async () => {
        if (!magicLinkEmail || magicLinkLoading || magicLinkCooldown > 0) return;
        await sendMagicLinkRequest(magicLinkEmail);
    };

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-[60]" onClose={() => { 
                clearErrors();
                onClose(); 
            }}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 modal-backdrop" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="relative bg-white dark:bg-neutral-900 rounded-xl shadow-2xl max-w-md w-full transform transition-all overflow-hidden">
                                <AnimatePresence>
                                    {socialStatus && (
                                        <SocialLoginStatus 
                                            status={socialStatus} 
                                            provider={socialProvider}
                                            errorMessage={error ? t(error) : undefined} 
                                        />
                                    )}
                                </AnimatePresence>
                                <div className="px-6 pb-6 pt-10">
                                    <div className="flex justify-center mb-8 h-8">
                                        <h2 className="relative text-2xl font-semibold text-text-primary dark:text-neutral-100">
                                            <div className="absolute right-full mr-3 flex items-center gap-2 whitespace-nowrap h-full">
                                                <Logo className="w-7 h-7" />
                                                <span className="w-4 h-4 text-neutral-300 dark:text-neutral-600" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_89a1da25.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_89a1da25.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                                            </div>
                                            {t("auth.signIn")}
                                        </h2>
                                    </div>

                                    {view === 'options' ? (
                                        <div className="space-y-4">
                                            <SocialLoginButtons onSocialLogin={handleSocialLogin} isLoading={isLoading} />

                                            <button
                                                type="button"
                                                onClick={() => setView('email')}
                                                disabled={isLoading}
                                                className="relative flex items-center justify-center w-full px-4 py-3 border border-neutral-200 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-750 transition-colors disabled:opacity-50 disabled:cursor-not-allowed group"
                                            >
                                                <div className="absolute left-4 text-neutral-500 dark:text-neutral-400">
                                                    <span className="w-5 h-5" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_17b85f1b.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_17b85f1b.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                                                </div>
                                                <span className="text-sm font-medium text-neutral-700 dark:text-neutral-200">
                                                    {t("auth.continueWithEmail", "Continue with Email")}
                                                </span>
                                            </button>
                                        </div>
                                    ) : view === 'email' ? (
                                        <form
                                            id="dashbal-user-login-form"
                                            name="dashbal-user-login-form"
                                            className="space-y-4"
                                            onSubmit={handleSubmit(onSubmit)}
                                        >
                                            <div className="flex items-center mb-4">
                                                <button
                                                    type="button"
                                                    onClick={handleBack}
                                                    className="text-sm text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 flex items-center absolute left-6 top-10"
                                                >
                                                    <span className="w-5 h-5" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_8a57af8b.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_8a57af8b.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                                                </button>
                                            </div>

                                            {/* Error Message */}
                            <AnimatePresence>
                                {error && (
                                    <motion.div
                                        initial={{ opacity: 0, y: -8 }}
                                        animate={{
                                            opacity: 1,
                                            y: 0,
                                            x: [0, -3, 3, -3, 3, 0]
                                        }}
                                        exit={{ opacity: 0, scale: 0.95 }}
                                        transition={{ duration: 0.2 }}
                                        className="mb-6 p-4 rounded-xl bg-red-50 dark:bg-red-500/10 border border-red-100 dark:border-red-500/20"
                                    >
                                        <div className="flex items-start gap-3">
                                            <span className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_3c00880f.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_3c00880f.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                                            <div className="flex-1">
                                                <p className="text-sm font-medium text-red-700 dark:text-red-400 leading-snug">
                                                    {t(error)}
                                                </p>
                                                {error === 'userErrors.auth.accountNotFound' && (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            clearError();
                                                            if (onSwitchToRegister) {
                                                                onSwitchToRegister();
                                                            } else {
                                                                setSearchParams({ ...Object.fromEntries(searchParams), modal: "register" });
                                                            }
                                                        }}
                                                        className="mt-2 block text-xs font-bold text-red-800 dark:text-red-300 hover:underline underline-offset-2"
                                                    >
                                                        {t("userErrors.auth.signUpNow")}
                                                    </button>
                                                )}
                                                {error === 'userErrors.auth.invalidCredentials' && (
                                                    <Link
                                                        to="/forgot-password"
                                                        className="mt-2 block text-xs font-bold text-red-800 dark:text-red-300 hover:underline underline-offset-2"
                                                        onClick={() => clearError()}
                                                    >
                                                        {t("userErrors.auth.resetPasswordLink")}
                                                    </Link>
                                                )}
                                            </div>
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                                            <div>
                                                <label htmlFor="email" className="block text-sm font-medium text-text-primary dark:text-neutral-200">
                                                    {t("auth.email")}
                                                </label>
                                                <div className="mt-1">
                                                    <input
                                                        {...register("email", {
                                                            onChange: () => {
                                                                clearError();
                                                                clearErrors("email");
                                                            }
                                                        })}
                                                        id="email"
                                                        name="email"
                                                        type="email"
                                                        autoComplete="email"
                                                        className={`input ${(errors.email || error === 'userErrors.auth.invalidCredentials' || error === 'userErrors.auth.accountNotFound') ? "input-error" : ""}`}
                                                        placeholder={t("auth.emailPlaceholder") as string}
                                                        autoFocus
                                                    />
                                                    {errors.email && (
                                                        <p className="mt-1 text-sm text-red-600 dark:text-red-400 font-medium">{errors.email.message}</p>
                                                    )}
                                                </div>
                                            </div>

                                            <div>
                                                <label htmlFor="password" className="block text-sm font-medium text-text-primary dark:text-neutral-200">
                                                    {t("auth.password")}
                                                </label>
                                                <div className="mt-1">
                                                    <input
                                                        {...register("password", {
                                                            onChange: () => {
                                                                clearError();
                                                                clearErrors("password");
                                                            }
                                                        })}
                                                        id="password"
                                                        name="password"
                                                        type="password"
                                                        autoComplete="current-password"
                                                        className={`input ${(errors.password || error === 'userErrors.auth.invalidCredentials') ? "input-error" : ""}`}
                                                        placeholder={t("auth.passwordPlaceholder") as string}
                                                    />
                                                    {errors.password && (
                                                        <p className="mt-1 text-sm text-red-600 dark:text-red-400 font-medium">{errors.password.message}</p>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="flex items-center justify-between">
                                                <div className="text-sm">
                                                    <Link
                                                        to="/forgot-password"
                                                        className="font-medium text-accent-primary hover:text-accent-primary/80"
                                                    >
                                                        {t("auth.forgotPassword")}
                                                    </Link>
                                                </div>
                                            </div>

                                            <button
                                                type="submit"
                                                disabled={isLoading}
                                                className="btn-primary w-full py-3"
                                            >
                                                {isLoading ? (
                                                    <div className="flex items-center justify-center">
                                                        <div className="spinner mr-2" />
                                                        {t("actions.loading")}
                                                    </div>
                                                ) : (
                                                    t("auth.signIn")
                                                )}
                                            </button>

                                            <div className="relative my-2">
                                                <div className="absolute inset-0 flex items-center">
                                                    <div className="w-full border-t border-neutral-200 dark:border-neutral-700" />
                                                </div>
                                                <div className="relative flex justify-center text-xs">
                                                    <span className="bg-white dark:bg-neutral-900 px-2 text-neutral-500 dark:text-neutral-400">
                                                        {t("auth.or", "or")}
                                                    </span>
                                                </div>
                                            </div>

                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setMagicLinkError(null);
                                                    setMagicLinkSent(false);
                                                    setView('magiclink');
                                                }}
                                                className="w-full px-4 py-2.5 text-sm font-medium text-accent-primary hover:text-accent-primary/80 border border-neutral-200 dark:border-neutral-700 rounded-lg hover:bg-neutral-50 dark:hover:bg-neutral-800 transition-colors"
                                            >
                                                {t("auth.useMagicLink", "Sign in with magic link")}
                                            </button>
                                        </form>
                                    ) : (
                                        <div className="space-y-4">
                                            <div className="flex items-center mb-4">
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setMagicLinkError(null);
                                                        setMagicLinkSent(false);
                                                        setView('email');
                                                    }}
                                                    className="text-sm text-neutral-500 hover:text-neutral-700 dark:text-neutral-400 dark:hover:text-neutral-200 flex items-center absolute left-6 top-10"
                                                >
                                                    <span className="w-5 h-5" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_8a57af8b.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_8a57af8b.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                                                </button>
                                            </div>

                                            {!magicLinkSent ? (
                                                <form onSubmit={handleSendMagicLink} className="space-y-4">
                                                    <p className="text-sm text-neutral-600 dark:text-neutral-400 text-center">
                                                        {t("auth.magicLinkDescription", "Enter your email and we'll send you a secure sign-in link. No password needed.")}
                                                    </p>

                                                    {magicLinkError && (
                                                        <motion.div
                                                            initial={{ opacity: 0, y: -8 }}
                                                            animate={{ opacity: 1, y: 0 }}
                                                            className="p-3 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-100 dark:border-red-500/20"
                                                        >
                                                            <p className="text-sm font-medium text-red-700 dark:text-red-400">{magicLinkError}</p>
                                                        </motion.div>
                                                    )}

                                                    <div>
                                                        <label htmlFor="magic-link-email" className="block text-sm font-medium text-text-primary dark:text-neutral-200">
                                                            {t("auth.email")}
                                                        </label>
                                                        <div className="mt-1">
                                                            <input
                                                                id="magic-link-email"
                                                                type="email"
                                                                autoComplete="email"
                                                                value={magicLinkEmail}
                                                                onChange={(e) => {
                                                                    setMagicLinkEmail(e.target.value);
                                                                    setMagicLinkError(null);
                                                                    setMagicLinkCooldown(0);
                                                                }}
                                                                className={`input ${magicLinkError ? "input-error" : ""}`}
                                                                placeholder={t("auth.emailPlaceholder") as string}
                                                                autoFocus
                                                            />
                                                        </div>
                                                    </div>

                                                    <button
                                                        type="submit"
                                                        disabled={magicLinkLoading || !magicLinkEmail || magicLinkCooldown > 0}
                                                        className={`btn-primary w-full py-3 ${magicLinkCooldown > 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                    >
                                                        {magicLinkLoading ? (
                                                            <div className="flex items-center justify-center">
                                                                <div className="spinner mr-2" />
                                                                {t("actions.loading")}
                                                            </div>
                                                        ) : magicLinkCooldown > 0 ? (
                                                            t("auth.resendIn", { seconds: magicLinkCooldown, defaultValue: `Resend in ${magicLinkCooldown}s` })
                                                        ) : (
                                                            t("auth.sendMagicLink", "Send magic link")
                                                        )}
                                                    </button>
                                                </form>
                                            ) : (
                                                <motion.div
                                                    initial={{ opacity: 0, scale: 0.95 }}
                                                    animate={{ opacity: 1, scale: 1 }}
                                                    className="text-center space-y-4 py-4"
                                                >
                                                    <div className="mx-auto w-12 h-12 rounded-full bg-green-100 dark:bg-green-500/10 flex items-center justify-center">
                                                        <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                                        </svg>
                                                    </div>
                                                    <div>
                                                        <h3 className="text-lg font-semibold text-text-primary dark:text-neutral-100">
                                                            {t("auth.checkYourEmail")}
                                                        </h3>
                                                        <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
                                                            {t("auth.magicLinkSentTo", "We've sent a sign-in link to:")}
                                                        </p>
                                                        <p className="mt-1 text-sm font-medium text-text-primary dark:text-neutral-200">
                                                            {magicLinkEmail}
                                                        </p>
                                                    </div>
                                                    <p className="text-xs text-neutral-500 dark:text-neutral-500">
                                                        {t("auth.magicLinkExpiry", "The link will expire in 15 minutes.")}
                                                    </p>

                                                    {magicLinkError && (
                                                        <div className="px-3 py-2 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-100 dark:border-red-500/20">
                                                            <p className="text-xs font-medium text-red-700 dark:text-red-400">{magicLinkError}</p>
                                                        </div>
                                                    )}

                                                    <button
                                                        type="button"
                                                        onClick={handleResendMagicLink}
                                                        disabled={magicLinkLoading || magicLinkCooldown > 0}
                                                        className={`text-sm font-medium text-accent-primary hover:text-accent-primary/80 ${(magicLinkLoading || magicLinkCooldown > 0) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                    >
                                                        {magicLinkLoading ? (
                                                            <span className="inline-flex items-center">
                                                                <div className="spinner mr-2" />
                                                                {t("actions.loading")}
                                                            </span>
                                                        ) : magicLinkCooldown > 0 ? (
                                                            t("auth.resendIn", { seconds: magicLinkCooldown, defaultValue: `Resend in ${magicLinkCooldown}s` })
                                                        ) : (
                                                            t("auth.resendMagicLink", "Send again")
                                                        )}
                                                    </button>
                                                </motion.div>
                                            )}
                                        </div>
                                    )}

                                    <div className="text-center mt-6 pt-4 border-t border-neutral-100 dark:border-neutral-800">
                                        <span className="text-sm text-neutral-600 dark:text-neutral-400">
                                            {t("auth.noAccount")}{" "}
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    if (onSwitchToRegister) {
                                                        onSwitchToRegister();
                                                    } else {
                                                        setSearchParams({ ...Object.fromEntries(searchParams), modal: "register" });
                                                    }
                                                }}
                                                className="font-medium text-accent-primary hover:text-accent-primary/80"
                                            >
                                                {t("auth.signUp")}
                                            </button>
                                        </span>
                                    </div>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};
